import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer, Marker, useMap } from "react-leaflet";
import { MapTiles } from "@/components/MapTiles";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { Search, LocateFixed, Loader2, MapPin, X, Plus, Minus } from "lucide-react";
import { fetchEventsByIds, fetchCalendarEvents, fetchDiscoverEvents, fetchMapEvents, fetchActiveBoostIds, PLACEHOLDER_IMAGE } from "../lib/events";
import { Logo } from "../components/Logo";
import { NearbyCard } from "../components/NearbyCard";
import { EventCard } from "../components/EventCard";
import { AppMenu } from "../components/AppMenu";
import { NotificationsBell } from "../components/NotificationsBell";
import { BottomSheet } from "../components/BottomSheet";
import { CountrySelector } from "../components/CountrySelector";
import { AdSlot } from "../components/AdSlot";
import { Skeleton } from "../components/ui/skeleton";
import { distanceKm, formatDistance, getPosition, readCachedPosition } from "../lib/geo";
import { getNavState, saveNavState } from "../lib/navState";
import { useCountry } from "../context/CountryContext";
import { useTheme } from "../context/ThemeContext";
import { useWideScreen } from "../hooks/useWideScreen";

// Default fallback viewport when there is no location/restored view: England-centred
// at a moderate zoom that shows a broad part of the UK (nationwide feel) while still
// exposing individual pins/smaller clusters. Fixed zoom keeps it consistent across
// phone sizes; centerWithInset offsets for the bottom drawer.
const DEFAULT_CENTER = [52.8, -1.6];
const DEFAULT_ZOOM = 6.5;
// Default map viewport per selected country (map still pans/zooms freely after).
const COUNTRY_VIEWS = {
  GB: { center: [52.8, -1.6], zoom: 6.5 },
  AU: { center: [-25.3, 133.8], zoom: 3.7 },
  NZ: { center: [-41.0, 173.3], zoom: 5 },
  US: { center: [39.8, -98.6], zoom: 3.8 },
};
const countryView = (code) => COUNTRY_VIEWS[code] || { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
const CATEGORIES = [
  "All",
  "Live Now",
  "Coming Up",
  "Cars",
  "Motorsport",
  "Motorcycles",
  "Shows",
  "Meets & Coffee",
  "Track Days",
];

// "Coming Up" / "Happening near you" relevance window: currently-live events plus
// events whose start falls within the next 30 days (rolling from now). Keeps both
// sections to genuinely relevant events instead of distant-future ones, while
// preserving LIVE NOW handling (live events are always considered relevant).
const COMING_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
function inComingUp(e, now) {
  if (e.live) return true;
  const s = e.start_iso ? new Date(e.start_iso).getTime() : null;
  return s != null && s >= now && s <= now + COMING_WINDOW_MS;
}

// Preview-only mock Featured card (shown ONLY when there are no real active
// boosts) so the design can be judged. Never saved to Supabase, never mixed
// into All Events / Happening Near You, and not clickable.
const PREVIEW_FEATURED = {
  id: "__preview_featured__",
  name: "Sunset Coast Car Meet",
  category: "Cars",
  location: "Brighton Seafront",
  image: PLACEHOLDER_IMAGE,
  date: "12 Jul 2026",
  is_free: true,
};

// Insert ~one advertisement after every N event cards in the All Events feed.
const AD_EVERY = 6;
function buildFeedWithAds(list) {
  const out = [];
  list.forEach((ev, i) => {
    out.push({ type: "event", ev });
    if ((i + 1) % AD_EVERY === 0) out.push({ type: "ad" });
  });
  // Preview: ensure exactly one ad is visible even with few events.
  if (!out.some((x) => x.type === "ad")) out.push({ type: "ad" });
  return out;
}

const eventIcon = L.divIcon({
  className: "rr-marker",
  html: '<div class="rr-pin"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});
const eventIconFeatured = L.divIcon({
  className: "rr-marker",
  html: '<div class="rr-pin-featured"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});
const eventIconLive = L.divIcon({
  className: "rr-marker",
  html: '<div class="rr-pin-live"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});
const userIcon = L.divIcon({
  className: "rr-marker",
  html: '<div class="rr-user-pin"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function MapRef({ onReady }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
}

// Loads map markers for the CURRENTLY VISIBLE map area only, so the map scales to
// thousands of events without a global cap. Fires once for the initial view, then
// after every pan/zoom — leaflet's `moveend` covers both — debounced (400ms) so it
// never queries Supabase mid-gesture. Bounds are padded ~20% so pins just off-screen
// are ready when the user nudges the map.
function MapBoundsLoader({ onBounds }) {
  const map = useMap();
  useEffect(() => {
    let t;
    const fire = () => {
      const b = map.getBounds().pad(0.2);
      onBounds({
        north: b.getNorth(),
        south: b.getSouth(),
        east: b.getEast(),
        west: b.getWest(),
      });
    };
    const debounced = () => {
      clearTimeout(t);
      t = setTimeout(fire, 400);
    };
    map.on("moveend", debounced);
    fire(); // initial visible area
    return () => {
      clearTimeout(t);
      map.off("moveend", debounced);
    };
  }, [map, onBounds]);
  return null;
}

// Reuses the established MiniMapFix pattern (EventDetails.jsx): the main map can
// mount with a stale container size while the mobile viewport/layout settles, so
// recompute its size shortly after mount and on genuine resize/orientation change.
// Does NOT touch zoom/centering.
function MapSizeFix() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    const t1 = setTimeout(fix, 200);
    const t2 = setTimeout(fix, 600);
    window.addEventListener("resize", fix);
    window.addEventListener("orientationchange", fix);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fix);
      window.removeEventListener("orientationchange", fix);
    };
  }, [map]);
  return null;
}

function ClusterLayer({ events, onSelect, featuredIds }) {
  const map = useMap();
  const groupRef = useRef(null);
  // Confirmed live events get their own markers in a layer that is never clustered, so a
  // live pin is never swallowed by a numbered cluster on any filter (support #279162).
  const liveGroupRef = useRef(null);
  const liveMarkersRef = useRef(new Map());
  const markersRef = useRef(new Map());
  const onSelectRef = useRef(onSelect);

  useLayoutEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 55,
      iconCreateFunction: (cluster) =>
        L.divIcon({
          html: '<div class="rr-cluster">' + cluster.getChildCount() + '</div>',
          className: "rr-cluster-wrap",
          iconSize: L.point(42, 42),
        }),
    });
    groupRef.current = group;
    map.addLayer(group);
    const liveGroup = L.layerGroup();
    liveGroupRef.current = liveGroup;
    map.addLayer(liveGroup);
    const liveMarkerMap = liveMarkersRef.current;
    return () => {
      map.removeLayer(group);
      map.removeLayer(liveGroup);
      markersRef.current.forEach(({ marker }) => marker.off());
      markersRef.current.clear();
      liveMarkerMap.forEach((marker) => marker.off());
      liveMarkerMap.clear();
      groupRef.current = null;
      liveGroupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    const liveGroup = liveGroupRef.current;
    if (!group || !liveGroup) return;
    const markers = markersRef.current;
    const liveMarkers = liveMarkersRef.current;
    const seenLive = new Set();
    const seen = new Set();
    const additions = [];
    const removals = [];
    events.forEach((ev) => {
      if (ev.id == null || seen.has(ev.id) ||
          !Number.isFinite(ev.latitude) || !Number.isFinite(ev.longitude) ||
          ev.latitude < -90 || ev.latitude > 90 ||
          ev.longitude < -180 || ev.longitude > 180) return;
      const isFeat = featuredIds && featuredIds.has(ev.id);
      // Confirmed live events (not featured, occurrence dates loaded) get a marker of their
      // own in the non-clustered layer. They are deliberately left out of `seen`, so any
      // clustered marker for the same event is removed below.
      if (!isFeat && ev.live && !ev.occurrencePending) {
        if (seenLive.has(ev.id)) return;
        seenLive.add(ev.id);
        const liveExisting = liveMarkers.get(ev.id);
        if (liveExisting) {
          const at = liveExisting.getLatLng();
          if (at.lat !== ev.latitude || at.lng !== ev.longitude) liveExisting.setLatLng([ev.latitude, ev.longitude]);
        } else {
          const liveMarker = L.marker([ev.latitude, ev.longitude], { icon: eventIconLive, zIndexOffset: 500 });
          liveMarker.on("click", () => onSelectRef.current(ev.id));
          liveMarkers.set(ev.id, liveMarker);
          liveGroup.addLayer(liveMarker);
        }
        return;
      }
      seen.add(ev.id);
      const icon = isFeat ? eventIconFeatured : ev.live ? eventIconLive : eventIcon;
      const zIndexOffset = isFeat ? 1000 : ev.live ? 500 : 0;
      const existing = markers.get(ev.id);
      if (existing) {
        if (existing.latitude !== ev.latitude || existing.longitude !== ev.longitude) {
          removals.push(existing.marker);
          additions.push(existing.marker);
          existing.nextPosition = [ev.latitude, ev.longitude];
          existing.latitude = ev.latitude;
          existing.longitude = ev.longitude;
        }
        if (existing.icon !== icon) {
          existing.marker.setIcon(icon);
          existing.icon = icon;
        }
        if (existing.zIndexOffset !== zIndexOffset) {
          existing.marker.setZIndexOffset(zIndexOffset);
          existing.zIndexOffset = zIndexOffset;
        }
        return;
      }
      const marker = L.marker([ev.latitude, ev.longitude], { icon, zIndexOffset });
      marker.on("click", () => onSelectRef.current(ev.id));
      markers.set(ev.id, { marker, latitude: ev.latitude, longitude: ev.longitude, icon, zIndexOffset });
      additions.push(marker);
    });
    markers.forEach((entry, id) => {
      if (!seen.has(id)) {
        removals.push(entry.marker);
        entry.marker.off();
        markers.delete(id);
      }
    });
    liveMarkers.forEach((marker, id) => {
      if (!seenLive.has(id)) {
        liveGroup.removeLayer(marker);
        marker.off();
        liveMarkers.delete(id);
      }
    });
    if (removals.length) group.removeLayers(removals);
    markers.forEach((entry) => {
      if (entry.nextPosition) {
        entry.marker.setLatLng(entry.nextPosition);
        delete entry.nextPosition;
      }
    });
    if (additions.length) group.addLayers(additions);
  }, [map, events, featuredIds]);
  return null;
}

export default function Discover() {
  const navigate = useNavigate();
  const location = useLocation();
  const navKey = `discover:${location.key}`;
  const savedNav = getNavState(navKey);
  const { country } = useCountry();
  const wide = useWideScreen();
  const { resolved } = useTheme();
  const [feedEvents, setFeedEvents] = useState([]);
  const [nearbyPool, setNearbyPool] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(true);
  const [featuredRaw, setFeaturedRaw] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);

  // Map markers: events inside the visible map area (bounds-loaded, scales to
  // thousands) merged with boosted events (loaded globally so their pins stay
  // visible everywhere, exactly as before).
  const [boundsEvents, setBoundsEvents] = useState([]);
  const [boostedMapEvents, setBoostedMapEvents] = useState([]);
  const [boostIds, setBoostIds] = useState([]);
  // All Events renders incrementally so the mounted card/DOM count (and the image
  // signing it triggers) stays bounded no matter how large the DB grows.
  const [visibleCount, setVisibleCount] = useState(() => savedNav?.visibleCount ?? 12);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const loadingRef = useRef(false);
  // Hydrate from the last-known location so distance labels render immediately on
  // load (and even if live geolocation is slow or the permission prompt on this
  // origin hasn't been re-granted yet). Refined by the live fix below when it
  // resolves. Never fabricated — only a location the browser previously gave us.
  const [userPos, setUserPos] = useState(() => readCachedPosition());
  const [category, setCategory] = useState(() => savedNav?.category ?? "All");
  const firstCategoryRun = useRef(true);
  const [locating, setLocating] = useState(false);
  const [geoDenied, setGeoDenied] = useState(false);
  const [locationFailed, setLocationFailed] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const mapRef = useRef(null);
  const boundsReqRef = useRef(0);
  const boundsAbortRef = useRef(null);
  const firstCountryView = useRef(true);
  const mapInitedRef = useRef(false);
  const [collapseSignal, setCollapseSignal] = useState(0);
  const topBarRef = useRef(null);
  const [topInset, setTopInset] = useState(190);

  // Measure the top search/category controls so the expanded drawer stops below them.
  useLayoutEffect(() => {
    const measure = () => {
      if (topBarRef.current) {
        setTopInset(Math.round(topBarRef.current.getBoundingClientRect().bottom));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined" && topBarRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(topBarRef.current);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // Amount of map covered by the drawer in the default state — used to shift the
  // centre so important markers sit in the VISIBLE map band above the drawer.
  const MAP_BOTTOM_INSET = 140;
  const centerWithInset = (map, lat, lng, zoom, animate = true) => {
    if (!map) return;
    const z = zoom ?? map.getZoom();
    const pt = map.project([lat, lng], z).add([0, MAP_BOTTOM_INSET / 2]);
    const c = map.unproject(pt, z);
    map.setView(c, z, { animate });
  };

  // When the selected country changes, move the map to that country's default
  // view (skips the initial mount — whenCreated already applies the country view
  // on load). After this the map stays freely pannable/zoomable.
  useEffect(() => {
    if (firstCountryView.current) {
      firstCountryView.current = false;
      return;
    }
    const v = countryView(country);
    if (mapRef.current) centerWithInset(mapRef.current, v.center[0], v.center[1], v.zoom);
    setCollapseSignal((n) => n + 1); // collapse the drawer so the map is the focus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    let active = true;
    // Featured + map-boosted pins load by id (fast); the drawer feed and map bounds
    // load progressively on their own. No full-country dataset is fetched up front.
    fetchActiveBoostIds()
      .then((ids) => { if (active) setBoostIds(ids); })
      .catch(() => { if (active) setBoostIds([]); });
    getPosition()
      .then((pos) => {
        // Track location for distance sorting / Near You only — the map default
        // view is driven by the selected country, NOT GPS. The location button is
        // the deliberate way to jump to the user's physical position.
        setUserPos(pos);
      })
      .catch(() => {
        // Only surface the enable-location notice if we have no usable location
        // at all; if a cached one is hydrating the cards, don't nag.
        if (!readCachedPosition()) setGeoDenied(true);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Bounds-driven map loading: fetch only the events inside the visible map area
  // (debounced by MapBoundsLoader). A request sequence guard drops any out-of-order
  // (slower/older) response so panning quickly can't leave a stale marker set.
  // Category changes filter the shared bounds dataset locally, without a refetch.
  useLayoutEffect(() => () => {
    boundsReqRef.current += 1;
    boundsAbortRef.current?.abort();
  }, []);
  const handleBounds = useCallback((bounds) => {
    const seq = ++boundsReqRef.current;
    boundsAbortRef.current?.abort();
    const controller = new AbortController();
    boundsAbortRef.current = controller;
    const onProgress = (evs) => {
      if (seq !== boundsReqRef.current || controller.signal.aborted) return;
      setBoundsEvents((previous) => {
        if (seq !== boundsReqRef.current || controller.signal.aborted) return previous;
        const merged = new Map(previous.filter((event) =>
          event.latitude >= bounds.south && event.latitude <= bounds.north &&
          (bounds.west <= bounds.east
            ? event.longitude >= bounds.west && event.longitude <= bounds.east
            : event.longitude >= bounds.west || event.longitude <= bounds.east)
        ).map((event) => [event.id, event]));
        evs.forEach((event) => {
          const existing = merged.get(event.id);
          if (!event.occurrencePending || !existing || existing.occurrencePending) merged.set(event.id, event);
        });
        return [...merged.values()];
      });
    };
    fetchMapEvents(bounds, null, { signal: controller.signal, onProgress })
      .then((evs) => {
        if (seq === boundsReqRef.current && !controller.signal.aborted) setBoundsEvents(evs);
      })
      .catch(() => {
        // Retain existing pins on failure or cancellation.
      });
  }, []);
  // Boosted pins stay visible everywhere (unchanged behaviour): load the boosted
  // events globally, independent of the visible bounds, and merge them into the map
  // marker set below.
  useEffect(() => {
    if (!boostIds.length) {
      setBoostedMapEvents([]);
      return;
    }
    fetchMapEvents(null, boostIds)
      .then(setBoostedMapEvents)
      .catch(() => setBoostedMapEvents([]));
  }, [boostIds]);

  // Featured carousel events load by id only (with covers) — independent of the
  // feed and never blocking the initial Discover load.
  useEffect(() => {
    if (!boostIds.length) {
      setFeaturedRaw([]);
      return;
    }
    let active = true;
    fetchEventsByIds(boostIds)
      .then((rows) => { if (active) setFeaturedRaw(rows); })
      .catch(() => { if (active) setFeaturedRaw([]); });
    return () => { active = false; };
  }, [boostIds]);

  // Reset the incremental window when the category (event set) changes — but NOT
  // on the initial mount, so a restored list depth (Back navigation) survives.
  useEffect(() => {
    if (firstCategoryRun.current) {
      firstCategoryRun.current = false;
      return;
    }
    setVisibleCount(12);
    setLoadingMore(false);
    loadingRef.current = false;
  }, [category]);

  // Persist the selected category + how far the list is expanded for this history
  // entry, so pressing Back rebuilds the same view (paired with the drawer/scroll
  // restore in BottomSheet).
  useEffect(() => {
    saveNavState(navKey, { category, visibleCount });
  }, [category, visibleCount, navKey]);

  const feedFiltered = useMemo(() => {
    const now = Date.now();
    let list;
    if (category === "All") {
      list = feedEvents;
    } else if (category === "Coming Up") {
      list = feedEvents.filter((e) => inComingUp(e, now));
    } else if (category === "Live Now") {
      list = feedEvents.filter((e) => e.live);
    } else {
      list = feedEvents.filter((e) => e.category === category);
    }
    // Discover's All Events is location-aware (nearest → furthest) when the visitor's
    // position is known, so it reads differently from the Search page's purely
    // chronological list. Without a location it falls back to chronological
    // (soonest → latest) by each event's current/next occurrence (start_iso).
    let sorted;
    if (userPos) {
      const dist = (e) =>
        typeof e.latitude === "number" && typeof e.longitude === "number"
          ? distanceKm(userPos.lat, userPos.lng, e.latitude, e.longitude)
          : Infinity;
      sorted = [...list].sort((a, b) => dist(a) - dist(b));
    } else {
      sorted = [...list].sort((a, b) => {
        const ta = a.start_iso ? new Date(a.start_iso).getTime() : Infinity;
        const tb = b.start_iso ? new Date(b.start_iso).getTime() : Infinity;
        return ta - tb;
      });
    }
    // Live-now events lead the list (support #279162), then the order above.
    return [...sorted.filter((e) => e.live), ...sorted.filter((e) => !e.live)];
  }, [feedEvents, category, userPos]);

  // Progressive, Calendar-style load: stream every page for the country into
  // feedEvents (first 12 fast, the rest append in the background). Category filtering
  // is client-side (feedFiltered) over the loaded set — no refetch, scalable.
  useEffect(() => {
    const controller = new AbortController();
    setFeedEvents([]);
    setFeedLoading(true);
    // Render progressively (pages of 200, so only a few steps per country) rather than
    // blocking on the whole list.
    const publish = (rows) => {
      if (controller.signal.aborted) return;
      setFeedEvents(rows);
      setFeedLoading(false);
    };
    fetchCalendarEvents(country, publish, 0, [], controller.signal)
      .then(publish)
      .catch(() => { if (!controller.signal.aborted) setFeedLoading(false); });
    return () => controller.abort();
  }, [country]);

  // Happening Near You draws from its OWN complete, one-shot dataset (not the
  // progressively-streaming feed), so the strip is stable and doesn't reshuffle or
  // stay half-empty while the feed pages in.
  useEffect(() => {
    const controller = new AbortController();
    setNearbyPool([]);
    setNearbyLoading(true);
    fetchDiscoverEvents(country, () => {}, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setNearbyPool(rows); })
      .catch(() => {})
      .finally(() => { if (!controller.signal.aborted) setNearbyLoading(false); });
    return () => controller.abort();
  }, [country]);
  const labelFor = (ev) =>
    userPos && typeof ev.latitude === "number" && typeof ev.longitude === "number"
      ? formatDistance(distanceKm(userPos.lat, userPos.lng, ev.latitude, ev.longitude))
      : null;

  // Map markers depend ONLY on the event SET + category — never on the distance
  // sort. Passing the distance-sorted `filtered` (a new array whenever userPos
  // changes, e.g. when geolocation resolves ~1s after load) would force Leaflet
  // to clear and rebuild every marker + re-cluster, an O(n) main-thread task that
  // scales with event count and blocks taps. Sort order is irrelevant on a map.
  const mapEvents = useMemo(() => {
    // Map marker set = visible-area (bounds-loaded) events merged with the
    // always-visible boosted events (deduped by id), then category-filtered.
    let all = boundsEvents;
    if (boostedMapEvents.length) {
      const seen = new Set(boundsEvents.map((e) => e.id));
      all = boundsEvents.concat(boostedMapEvents.filter((e) => !seen.has(e.id)));
    }
    if (category === "All") return all;
    // Live Now / Coming Up depend on each event's CURRENT occurrence. A pin that just
    // arrived from a pan is drawn from its row dates (occurrencePending) and a recurring
    // event can look live until its occurrences load, so it popped onto the map and
    // vanished again on every pan. Wait for the confirmed occurrence before showing it;
    // pins already confirmed keep their place (support, Oct 2026).
    if (category === "Coming Up") {
      const now = Date.now();
      return all.filter((e) => !e.occurrencePending && inComingUp(e, now));
    }
    if (category === "Live Now") return all.filter((e) => !e.occurrencePending && e.live);
    return all.filter((e) => e.category === category);
  }, [boundsEvents, boostedMapEvents, category]);

  // Happening near you: nearest current/upcoming events (nearest → furthest) from the
  // SAME 30-day/live window as Coming Up. Drawn from the FULL loaded set and is
  // INDEPENDENT of the selected category chip, so the strip stays stable while you
  // filter the main list. Falls back to chronological when location is unavailable. Max 20.
  const nearby = useMemo(() => {
    const now = Date.now();
    let list;
    if (category === "Live Now") {
      list = nearbyPool.filter((e) => e.live);
    } else if (category === "All" || category === "Coming Up") {
      list = nearbyPool.filter((e) => inComingUp(e, now));
    } else {
      list = nearbyPool.filter((e) => e.category === category && inComingUp(e, now));
    }
    if (userPos) {
      const dist = (e) =>
        typeof e.latitude === "number" && typeof e.longitude === "number"
          ? distanceKm(userPos.lat, userPos.lng, e.latitude, e.longitude)
          : Infinity;
      list = [...list].sort((a, b) => dist(a) - dist(b));
    } else {
      list = [...list].sort((a, b) => {
        const ta = a.start_iso ? new Date(a.start_iso).getTime() : Infinity;
        const tb = b.start_iso ? new Date(b.start_iso).getTime() : Infinity;
        return ta - tb;
      });
    }
    // Live-now events lead the strip (support #279162), then the order above.
    list = [...list.filter((e) => e.live), ...list.filter((e) => !e.live)];
    return list.slice(0, 20);
  }, [nearbyPool, userPos, category]);

  // Featured = ALL currently-active boosted events, derived from the public events
  // set (never filtered by the current user/organiser/owner; identical for logged-in
  // and logged-out visitors). Nearest → furthest when the visitor's location is
  // known; otherwise a random shuffle so boosted advertisers get fair exposure/rotation.
  const featuredList = useMemo(() => {
    if (!featuredRaw.length) return [];
    let list = featuredRaw.filter((e) => (e.country_code || "GB").toUpperCase() === country);
    if (userPos) {
      const dist = (e) =>
        typeof e.latitude === "number" && typeof e.longitude === "number"
          ? distanceKm(userPos.lat, userPos.lng, e.latitude, e.longitude)
          : Infinity;
      list = [...list].sort((a, b) => dist(a) - dist(b));
    } else {
      list = [...list].sort(() => Math.random() - 0.5);
    }
    return list.slice(0, 20);
  }, [featuredRaw, country, userPos]);

  // Map marker "featured" styling spans ALL countries (boosted pins anywhere),
  // unlike the country-scoped Featured carousel in the drawer.
  const mapFeaturedIds = useMemo(() => new Set(boostIds), [boostIds]);

  const handleSelect = useCallback((id) => navigate(`/event/${id}`, { state: { backgroundLocation: location } }), [navigate, location]);

  // Grow the All Events window as the sentinel scrolls into view. Small rootMargin
  // so the sentinel is actually on-screen when it fires (a 400px prefetch loaded
  // the next batch before the indicator was ever visible). A double rAF paints the
  // "Loading more events…" row for a frame BEFORE the next cards reveal, so the
  // transition is genuinely perceivable: cards → Loading more events… → next cards.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (feedLoading || !el || visibleCount >= feedFiltered.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingRef.current) {
          loadingRef.current = true;
          setLoadingMore(true);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              setVisibleCount((c) => c + 12);
              setLoadingMore(false);
              loadingRef.current = false;
            })
          );
        }
      },
      { root: null, rootMargin: "80px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [feedFiltered.length, visibleCount, feedLoading]);

  const goToMyLocation = async () => {
    setLocating(true);
    try {
      const pos = userPos || (await getPosition());
      setUserPos(pos);
      setGeoDenied(false);
      setLocationFailed(false);
      if (mapRef.current) {
        centerWithInset(mapRef.current, pos.lat, pos.lng, 12);
      }
    } catch {
      setGeoDenied(true);
      setLocationFailed(true);
      setBannerDismissed(false);
    } finally {
      setLocating(false);
    }
  };

  return (
    <div
      data-testid="discover-page"
      className={
        wide
          ? "flex h-[var(--app-height,100dvh)] w-full flex-col overflow-hidden bg-[#050505]"
          : "relative h-[var(--app-height,100dvh)] w-full overflow-hidden bg-[#050505]"
      }
    >
      <h1 className="sr-only">RoadRnr — Discover car meets, motorcycle events, shows and track days near you</h1>

      {/* Header: overlay on mobile; static full-width bar on wide screens */}
      <div
        ref={topBarRef}
        className={
          wide
            ? "relative z-10 shrink-0 border-b border-white/10 bg-[#050505] px-6 pb-3 pt-3"
            : "absolute inset-x-0 top-0 z-[1300] px-4 pt-[max(0.75rem,env(safe-area-inset-top))]"
        }
      >
        <div className={wide ? "w-full" : "mx-auto max-w-md"}>
          <div className={wide ? "flex items-center gap-4" : "flex items-center justify-between"}>
            <Logo />
            {wide && (
              <button
                data-testid="discover-search-bar"
                onClick={() => navigate("/search")}
                className="flex flex-1 items-center gap-3 rounded-full border border-white/10 bg-[#0a0a0a] px-5 py-2.5 text-left transition-colors duration-200 active:border-white/25 hover:border-white/25"
              >
                <Search className="h-5 w-5 text-[#ff2a00]" />
                <span className="text-sm text-white/50">Search automotive events</span>
              </button>
            )}
            <div className="flex items-center gap-2">
              <CountrySelector />
              <NotificationsBell />
              <AppMenu />
            </div>
          </div>
          {!wide && (
            <button
              data-testid="discover-search-bar"
              onClick={() => navigate("/search")}
              className="mt-2 flex w-full items-center gap-3 rounded-full border border-white/10 bg-[#0a0a0a] px-5 py-3.5 text-left shadow-lg shadow-black/40 transition-colors duration-200 active:border-white/25"
            >
              <Search className="h-5 w-5 text-[#ff2a00]" />
              <span className="text-sm text-white/50">Search automotive events</span>
            </button>
          )}
          <div className={`hide-scrollbar flex gap-2 overflow-x-auto ${wide ? "mt-3 pb-1" : "-mx-4 -my-1 mt-2 px-4 py-2"}`}>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`map-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold shadow-md shadow-black/30 transition-colors duration-200 ${
                  c === "Live Now"
                    ? category === c
                      ? "border-[#FFB000] bg-[#FFB000] text-black"
                      : "border-[#FFB000]/40 bg-[#0a0a0a] text-[#FFB000]"
                    : category === c
                    ? "border-[#ff2a00] bg-[#ff2a00] text-white"
                    : "border-white/10 bg-[#0a0a0a] text-white/70"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Wide: LEFT event content (scrolls) + RIGHT contained map. Mobile: unchanged. */}
      {wide ? (
        <div className="flex min-h-0 flex-1 gap-4 px-6 pb-[calc(6rem+env(safe-area-inset-bottom))] md:max-lg:pb-[calc(5.25rem+env(safe-area-inset-bottom))] pt-4">
          <div data-testid="discover-wide-list" className="w-1/2 overflow-y-auto pr-1">
            {drawerContent()}
          </div>
          <div className="relative w-1/2 overflow-hidden rounded-3xl border border-white/10">
            {mapPane()}
          </div>
        </div>
      ) : (
        <>
          {mapPane()}
          <BottomSheet topInset={topInset} collapseSignal={collapseSignal}>
            {drawerContent()}
          </BottomSheet>
        </>
      )}
    </div>
  );

  function mapPane() {
    return (
    <>
      <MapContainer
        center={countryView(country).center}
        zoom={countryView(country).zoom}
        zoomControl={false}
        scrollWheelZoom={false}
        className={wide ? "h-full w-full" : "absolute inset-0 z-0 h-full w-full"}
      >
        <MapRef
          onReady={(m) => {
            mapRef.current = m;
            // Apply the selected country's default view ONCE, on first ready. This
            // fires on every re-render (inline handler), so guard it — otherwise
            // bounds-loading re-renders (setBoundsEvents on each pan/zoom) would keep
            // snapping the map back to the default position. Country changes are
            // handled separately by the [country] effect.
            if (mapInitedRef.current) return;
            mapInitedRef.current = true;
            const v = countryView(country);
            centerWithInset(m, v.center[0], v.center[1], v.zoom, false);
          }}
        />
        <MapSizeFix />
        <MapBoundsLoader onBounds={handleBounds} />
        <MapTiles theme={resolved} />
        <ClusterLayer events={mapEvents} onSelect={handleSelect} featuredIds={mapFeaturedIds} />
        {userPos && <Marker position={[userPos.lat, userPos.lng]} icon={userIcon} />}
      </MapContainer>

      {/* Map controls (stay in the visible map band above the drawer) */}
      <div
        className="absolute right-3 z-[1000] flex flex-col gap-2"
        style={{ top: wide ? "0.75rem" : "calc(env(safe-area-inset-top) + 184px)" }}
      >
        <button
          data-testid="map-zoom-in"
          onClick={() => mapRef.current?.zoomIn()}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0a0a0a] transition-transform duration-200 active:scale-90"
          aria-label="Zoom in"
        >
          <Plus className="h-5 w-5 text-white" />
        </button>
        <button
          data-testid="map-zoom-out"
          onClick={() => mapRef.current?.zoomOut()}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0a0a0a] transition-transform duration-200 active:scale-90"
          aria-label="Zoom out"
        >
          <Minus className="h-5 w-5 text-white" />
        </button>
        <button
          data-testid="current-location-btn"
          onClick={goToMyLocation}
          disabled={locating}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0a0a0a] transition-transform duration-200 active:scale-90"
          aria-label="Go to my location"
        >
          {locating ? (
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          ) : (
            <LocateFixed className="h-5 w-5 text-[#ff2a00]" />
          )}
        </button>
      </div>
    </>
    );
  }

  function drawerContent() {
    return (
        <div className={wide ? "w-full" : "mx-auto max-w-md"}>
          {/* Non-blocking location notice */}
          {geoDenied && !bannerDismissed && (
            <div
              data-testid="location-notice"
              className="mx-5 mt-1 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121212] px-4 py-3"
            >
              <MapPin className="h-5 w-5 shrink-0 text-[#ff2a00]" />
              <p className="flex-1 text-[13px] leading-snug text-white/70">
                {locationFailed
                  ? "Location unavailable. Try Safari/Chrome or check location permissions. You can still browse events."
                  : "Enable location to sort events by distance from you."}
              </p>
              <button
                data-testid="location-notice-enable"
                onClick={goToMyLocation}
                className="shrink-0 rounded-full bg-[#ff2a00] px-3 py-1.5 text-xs font-semibold text-white"
              >
                Enable
              </button>
              <button
                data-testid="location-notice-dismiss"
                onClick={() => setBannerDismissed(true)}
                className="shrink-0 text-white/40 transition-colors hover:text-white"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Happening near you */}
          <section className="pt-4">
            <div className="flex items-center justify-between px-5">
              <h2 className="font-display text-xl font-extrabold tracking-tight text-white">
                Happening near you
              </h2>
              <span className="text-[11px] font-medium text-white/50">
                {userPos ? "Sorted by distance" : `${nearby.length} events`}
              </span>
            </div>

            {nearbyLoading ? (
              <div className="mt-3 flex gap-3 overflow-hidden px-5">
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-[84px] w-[250px] shrink-0 rounded-2xl bg-white/10"
                  />
                ))}
              </div>
            ) : nearby.length === 0 ? (
              <div className="mx-5 mt-3 rounded-2xl border border-white/10 bg-[#121212] px-5 py-4 text-sm text-white/60">
                No events in this category yet.
              </div>
            ) : (
              <div
                data-testid="nearby-carousel"
                className="hide-scrollbar mt-3 flex snap-x snap-mandatory justify-start gap-3 overflow-x-auto px-5 scroll-pl-5"
              >
                {nearby.map((ev, i) => (
                  <NearbyCard
                    key={ev.id}
                    event={ev}
                    index={i}
                    distanceLabel={labelFor(ev)}
                  />
                ))}
                <div className="w-1 shrink-0" />
              </div>
            )}
          </section>

          {/* Featured — genuinely boosted events only; preview card when none */}
          <section data-testid="featured-section" className="pt-7 md:max-lg:pt-5">
            <div className="flex items-center justify-between px-5">
              <h2 className="font-display text-xl font-extrabold tracking-tight text-white">
                Featured
              </h2>
              <span className="text-[11px] font-medium text-white/50">
                {featuredList.length > 0 ? "Promoted" : "Preview"}
              </span>
            </div>

            {featuredList.length > 0 ? (
              <div
                data-testid="featured-carousel"
                className="hide-scrollbar mt-3 flex snap-x snap-mandatory justify-start gap-4 overflow-x-auto px-5 scroll-pl-5"
              >
                {featuredList.map((ev, i) => (
                  <EventCard
                    key={ev.id}
                    event={ev}
                    index={i}
                    variant="compact"
                    featured
                    distanceLabel={labelFor(ev)}
                  />
                ))}
                <div className="w-1 shrink-0" />
              </div>
            ) : (
              <div data-testid="featured-preview">
                <p className="px-5 pt-1 text-[12px] leading-snug text-white/40">
                  Preview only — no active boosts yet. Boosted events will look
                  like this.
                </p>
                <div className="hide-scrollbar mt-3 flex gap-4 overflow-x-auto px-5">
                  <EventCard event={PREVIEW_FEATURED} variant="compact" featured preview />
                  <div className="w-1 shrink-0" />
                </div>
              </div>
            )}
          </section>

          {/* All events */}
          <section className="px-5 pt-8 md:max-lg:pt-6">
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-white">
              All events
            </h2>
            {feedLoading ? (
              <div className="mt-4 grid grid-cols-2 gap-4">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[240px] rounded-3xl bg-white/5" />
                ))}
              </div>
            ) : feedFiltered.length === 0 ? (
              <p className="mt-4 text-sm text-white/50">
                No events in this category yet.
              </p>
            ) : (
              <div data-testid="all-events-grid" className="mt-4 grid grid-cols-2 gap-4">
                {buildFeedWithAds(feedFiltered.slice(0, visibleCount)).map((item, i) =>
                  item.type === "ad" ? (
                    <AdSlot key={`ad-${i}`} page="discover" />
                  ) : (
                    <EventCard
                      key={item.ev.id}
                      event={item.ev}
                      index={i}
                      distanceLabel={labelFor(item.ev)}
                    />
                  )
                )}
              </div>
            )}
            {/* Loading indicator lives INSIDE the drawer's scrollable content and
                above the map (opaque drawer, z-1200). Shown while a batch reveals
                or more remain; kept small/subtle. */}
            {!feedLoading && (visibleCount < feedFiltered.length || loadingMore) && (
              <div
                ref={loadMoreRef}
                data-testid="load-more-sentinel"
                className="flex items-center justify-center gap-2 py-4 text-sm font-medium text-white/50"
              >
                <Loader2 className="h-4 w-4 animate-spin text-[#ff2a00]" />
                Loading more events…
              </div>
            )}
          </section>
        </div>
    );
  }
}
